// Eine Sitzung in voller Länge — schiebt sich beim Klick auf eine Zeile von
// rechts über die Liste (Nico, 12.09.2026).
//
// Hier und NUR hier steht der Herzschlag: was der Besucher gerade macht, in
// historischer Reihenfolge, aufgebaut wie die Live-Seite selbst — links die
// Uhrzeit, daneben der Strang, rechts was passiert ist. Die Liste draußen
// zeigt Sitzungen; alles Feinere hätte sie zugeschüttet.
//
// Der Verlauf mischt drei Quellen in eine Spur:
//   Seitenaufrufe  aus analytics_events (mit gemessener Lesezeit)
//   Abschnitte     aus analytics_live_verlauf (je Wechsel ein Eintrag)
//   Klicks         aus analytics_events
// Die Lesezeit einer Seite trifft erst ein, wenn sie verlassen wird — beim
// laufenden Abschnitt steht deshalb die Zeit, die er dort schon steht, und
// dazu, dass die gemessene noch folgt. Zwei verschiedene Größen, nie dieselbe
// Zahl in zwei Kleidern.
//
// Unten steht, was es NICHT gibt (kein Name, keine Firma, keine IP): Wer das
// weglässt, weckt Erwartungen, die die Daten nicht einlösen.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Info, Inbox, Loader2, X } from 'lucide-react';
import { getLiveSitzung, type LiveSitzung } from '@/lib/analytics-api';
import { abschnittText, AktionIcon, aktionText, dauer, Herkunft, ruhtGerade, uhrzeit } from './bausteine';

type Eintrag =
  | { art: 'seite'; at: string; pfad: string | null; lesezeit_s: number | null; scroll: number | null }
  | { art: 'abschnitt'; at: string; name: string | null; pfad: string | null; scroll: number | null; formular: boolean }
  | { art: 'klick'; at: string; name: string };

/** Die drei Quellen in eine Spur, älteste zuerst. */
function verlaufBauen(data: LiveSitzung): Eintrag[] {
  const spur: Eintrag[] = [];
  for (const s of data.weg) {
    spur.push({ art: 'seite', at: s.at, pfad: s.pfad, lesezeit_s: s.lesezeit_s, scroll: s.scroll });
  }
  const puls = data.puls || [];
  puls.forEach((p, i) => {
    // Ein Eintrag ohne Abschnitt markiert nur den Seitenwechsel — den trägt
    // schon der Seitenaufruf. Ihn trotzdem aufzunehmen hat eine zweite Zeile
    // erzeugt, die unter „/fenster" nochmal „auf /fenster" sagte.
    if (!p.abschnitt) return;
    if (i > 0 && puls[i - 1].abschnitt === p.abschnitt && puls[i - 1].pfad === p.pfad) return;
    spur.push({ art: 'abschnitt', at: p.at, name: p.abschnitt, pfad: p.pfad, scroll: p.scroll, formular: p.formular });
  });
  for (const a of data.aktionen) spur.push({ art: 'klick', at: a.at, name: a.name });
  // Nach Zeitstempel, nicht nach Text: Die beiden Tabellen liefern
  // unterschiedlich viele Nachkommastellen, ein Stringvergleich waere darauf
  // hereingefallen.
  return spur.sort((x, y) => Date.parse(x.at) - Date.parse(y.at));
}

export default function Sitzung({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['live-sitzung', id],
    queryFn: () => getLiveSitzung(id),
    // Solange er da ist, läuft die Fläche mit; ist der Besuch vorbei, ändert
    // sich nichts mehr und Nachladen wäre reine Last.
    refetchInterval: (q) => (q.state.data?.online ? 5_000 : false),
    refetchIntervalInBackground: false,
    // Beim Öffnen IMMER frisch holen. Ohne das kam beim zweiten Klick auf
    // dieselbe Sitzung der Stand aus dem Cache — stand dort einmal
    // online:false, lief nie wieder ein Nachladen an und die Fläche blieb für
    // immer eingefroren.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-[620px] flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Sitzung"
      >
        {isLoading && (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sitzung wird geladen …
          </div>
        )}
        {error && (
          <div className="p-6">
            <button type="button" onClick={onClose} className="mb-3 text-sm text-gray-500 underline">Schließen</button>
            <p className="text-sm text-red-700">{(error as Error).message}</p>
          </div>
        )}

        {data && (() => {
          const spur = verlaufBauen(data);
          // Wo er JETZT steht: der jüngste Eintrag, der ein Ort ist — ein
          // Abschnitt, wenn der Tracker einen erkannt hat, sonst der
          // Seitenaufruf selbst. Ein Klick ist kein Ort, der zählt hier nicht.
          let laeuftIdx = -1;
          if (data.online && data.jetzt) {
            for (let i = spur.length - 1; i >= 0; i--) {
              if (spur[i].art !== 'klick') { laeuftIdx = i; break; }
            }
          }
          const ruht = ruhtGerade(data.jetzt);
          // Scrolltiefe ist dieselbe Größe, ob gemessen oder gerade gemeldet.
          const tiefsterScroll = data.jetzt?.scroll != null
            ? Math.max(data.summe.scroll_max ?? 0, data.jetzt.scroll)
            : data.summe.scroll_max;

          return (
          <>
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Sitzung</div>
                  <h2 className="mt-0.5 truncate text-lg font-semibold text-gray-900">{data.site_name}</h2>
                </div>
                <button type="button" onClick={onClose} className="shrink-0 rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50" aria-label="Schließen" title="Schließen (Esc)">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                {data.online && ruht ? (
                  // Noch da heißt nicht: tut etwas. Der Tab ist offen, bewegt
                  // hat sich seit über einer Minute nichts.
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-400" /> Tab offen, bewegt sich seit {dauer(data.jetzt?.bewegt_s ?? 0)} nicht
                  </span>
                ) : data.online ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> noch auf der Seite
                  </span>
                ) : (
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">Besuch beendet</span>
                )}
                <span className="text-xs tabular-nums text-gray-500">seit {uhrzeit(data.seit)} Uhr</span>
                {/* Ohne diese Angabe stand in der Liste „vor 4 Minuten" und
                    hier „ist hier seit 24 Minuten" — beides stimmte, zusammen
                    ergab es keinen Sinn. */}
                <span className="text-xs tabular-nums text-gray-500">
                  · zuletzt gesehen {uhrzeit(data.zuletzt)} Uhr
                </span>
                {data.besuche > 1 && (
                  <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 ring-1 ring-amber-200">
                    {data.besuch_nr}. von {data.besuche} Besuchen heute
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-px border-b border-gray-100 bg-gray-100">
              {[
                { wert: String(data.summe.seiten), label: data.summe.seiten === 1 ? 'Seite' : 'Seiten', hoch: false },
                // „0 s gelesen" wäre bei einem laufenden Besuch irreführend: Es
                // ist nichts gemessen, nicht nichts passiert.
                { wert: data.summe.lesezeit_s > 0 ? dauer(data.summe.lesezeit_s) : (data.jetzt ? 'läuft' : '0 s'), label: 'gelesen', hoch: false },
                { wert: tiefsterScroll != null ? `${tiefsterScroll} %` : '—', label: 'tiefster Scroll', hoch: false },
                { wert: String(data.summe.kontaktklicks), label: 'Kontaktklicks', hoch: data.summe.kontaktklicks > 0 },
              ].map((k) => (
                <div key={k.label} className="bg-white px-4 py-3">
                  <div className={`text-lg font-semibold tabular-nums ${k.hoch ? 'text-blue-700' : 'text-gray-900'}`}>{k.wert}</div>
                  <div className="text-xs text-gray-500">{k.label}</div>
                </div>
              ))}
            </div>

            {data.anfrage && (
              <Link to={`/leads/${data.anfrage.id}`} className="flex items-center gap-2 border-b border-emerald-100 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100">
                <Inbox className="h-4 w-4" /> Daraus wurde eine Anfrage{data.anfrage.name ? `: ${data.anfrage.name}` : ''}
              </Link>
            )}

            <div className="border-b border-gray-100 px-5 py-4">
              <div className="mb-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Woher</div>
              <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm">
                <dt className="text-gray-500">Kanal</dt>
                <dd className="min-w-0"><Herkunft kanal={data.kanal} quelle={data.quelle} kampagne={null} /></dd>
                {data.keyword && (<><dt className="text-gray-500">Keyword</dt><dd className="text-gray-900">„{data.keyword}“</dd></>)}
                {data.kampagne && (<><dt className="text-gray-500">Kampagne</dt><dd className="break-words text-gray-900">{data.kampagne}</dd></>)}
                <dt className="text-gray-500">Einstieg</dt>
                <dd className="min-w-0"><code className="break-all rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-800">{data.einstieg || '—'}</code></dd>
                <dt className="text-gray-500">Gerät</dt>
                <dd className="text-gray-900">{data.device || '—'}{data.land ? ` · ${data.land}` : ''}</dd>
              </dl>
            </div>

            <div className="flex-1 px-5 py-4">
              <div className="mb-3 flex items-baseline justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Verlauf</span>
                <span className="text-[11px] text-gray-400">
                  {data.online ? 'läuft mit — aktualisiert sich alle 5 Sekunden' : 'abgeschlossen'}
                </span>
              </div>

              <ol className="grid grid-cols-[52px_1fr] gap-x-3">
                {spur.map((e, i) => {
                  const letzte = i === spur.length - 1;
                  const laeuft = i === laeuftIdx;
                  const strang = letzte ? 'pb-1' : 'border-l-2 border-gray-200 pb-4';

                  if (e.art === 'seite') {
                    return (
                      <li key={`${e.at}-${i}`} className="contents">
                        <span className={`pt-0.5 text-right text-xs tabular-nums ${laeuft ? 'font-semibold text-emerald-700' : 'text-gray-400'}`}>{uhrzeit(e.at)}</span>
                        <div className={`relative pl-5 ${strang}`}>
                          <span className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-[3px] border-white ${laeuft ? 'bg-emerald-500 ring-2 ring-emerald-100' : 'bg-gray-500'}`} />
                          <div className="flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="break-all text-sm font-medium text-gray-900">{e.pfad || '—'}</span>
                          </div>
                          {e.lesezeit_s != null ? (
                            <div className="mt-1.5 flex items-center gap-2.5">
                              <span className="shrink-0 text-xs tabular-nums text-gray-600">{dauer(e.lesezeit_s)} gelesen</span>
                              <span className="h-1 max-w-[150px] flex-1 rounded bg-gray-100">
                                <span className="block h-1 rounded bg-gray-500" style={{ width: `${e.scroll ?? 0}%` }} />
                              </span>
                              <span className="shrink-0 text-xs tabular-nums text-gray-500">{e.scroll != null ? `${e.scroll} %` : ''}</span>
                            </div>
                          ) : laeuft ? (
                            // Er ist hier, aber der Tracker hat auf dieser Seite
                            // keinen Abschnitt erkannt. Dann steht der Stand am
                            // Seitenaufruf selbst statt in einer zweiten Zeile,
                            // die dasselbe nochmal sagt.
                            <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">ist hier</span>
                              <span className="shrink-0 text-xs tabular-nums text-emerald-700">{dauer(data.jetzt?.pfad_s ?? 0)}</span>
                              <span className="h-1 max-w-[150px] flex-1 rounded bg-emerald-100">
                                <span className="block h-1 rounded bg-emerald-500 transition-[width] duration-700" style={{ width: `${data.jetzt?.scroll ?? 0}%` }} />
                              </span>
                              <span className="shrink-0 text-xs tabular-nums text-emerald-700">{data.jetzt?.scroll ?? 0} %</span>
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  }

                  if (e.art === 'klick') {
                    return (
                      <li key={`${e.at}-${i}`} className="contents">
                        <span className="pt-0.5 text-right text-xs tabular-nums text-gray-400">{uhrzeit(e.at)}</span>
                        <div className={`relative pl-5 ${strang}`}>
                          <span className="absolute -left-[4px] top-[7px] h-2 w-2 rounded-full border-2 border-white bg-blue-600" />
                          <span className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-1.5 py-0.5 text-[11px] font-medium text-white">
                            <AktionIcon name={e.name} /> {aktionText(e.name)}
                          </span>
                        </div>
                      </li>
                    );
                  }

                  // Abschnitt: die feine Spur zwischen zwei Seitenaufrufen.
                  // Wie lange er dort war, steht erst fest, wenn der nächste
                  // Eintrag da ist — beim laufenden zählt die Zeit hoch.
                  // Bis zum nächsten ORT, nicht bis zum nächsten Eintrag: Ein
                  // Klick beendet den Abschnitt nicht. Dagegen gerechnet stand
                  // dort „4 s" bei jemandem, der zehn Minuten in dem Abschnitt
                  // war und zwischendurch die Nummer angeklickt hat.
                  const naechster = spur.slice(i + 1).find((x) => x.art !== 'klick');
                  const bis = laeuft
                    ? (data.jetzt?.abschnitt_s ?? 0)
                    : naechster
                      ? Math.max(0, Math.round((Date.parse(naechster.at) - Date.parse(e.at)) / 1000))
                      : null;
                  const tiefe = laeuft ? (data.jetzt?.scroll ?? e.scroll) : e.scroll;
                  return (
                    <li key={`${e.at}-${i}`} className="contents">
                      <span className={`pt-0.5 text-right text-xs tabular-nums ${laeuft ? 'font-semibold text-emerald-700' : 'text-gray-400'}`}>{uhrzeit(e.at)}</span>
                      <div className={`relative pl-5 ${letzte ? 'pb-1' : `border-l-2 pb-4 ${laeuft ? 'border-emerald-200' : 'border-gray-200'}`}`}>
                        <span className={`absolute -left-[4px] top-[7px] h-2 w-2 rounded-full border-2 border-white ${laeuft ? 'bg-emerald-500 ring-2 ring-emerald-100' : e.formular ? 'bg-blue-400' : 'bg-gray-300'}`} />
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className={`text-[13px] ${laeuft ? 'font-semibold text-emerald-800' : 'text-gray-700'}`}>
                            {e.name ? `Abschnitt „${abschnittText(e.name)}“` : `auf ${e.pfad || 'der Seite'}`}
                          </span>
                          {laeuft && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">ist hier</span>}
                          {/* Das Formular-Zeichen sagt: In diesem Abschnitt STEHT
                              ein Formular. Nicht, dass er gerade tippt — das
                              weiß der Tracker nicht, und es zu behaupten wäre
                              die Art Zahl, auf die man sich verlässt. */}
                          {e.formular && <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">Formular im Blick</span>}
                          {bis != null && bis >= 3 && (
                            <span className={`text-xs tabular-nums ${laeuft ? 'text-emerald-700' : 'text-gray-500'}`}>{dauer(bis)}</span>
                          )}
                        </div>
                        {tiefe != null && (
                          <div className="mt-1 flex items-center gap-2.5">
                            <span className={`h-1 max-w-[150px] flex-1 rounded ${laeuft ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                              <span className={`block h-1 rounded transition-[width] duration-700 ${laeuft ? 'bg-emerald-500' : 'bg-gray-400'}`} style={{ width: `${tiefe}%` }} />
                            </span>
                            <span className={`shrink-0 text-xs tabular-nums ${laeuft ? 'text-emerald-700' : 'text-gray-500'}`}>{tiefe} %</span>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>

              {data.online && !data.jetzt && Date.now() - Date.parse(data.seit) > 30_000 && (
                // Ereignisse ja, Lebenszeichen nein: Dann läuft auf dieser Seite
                // noch die alte Fassung des Trackers (t.js liegt bis zu 24 h im
                // Cache). Das ehrlich hinschreiben statt so zu tun, als wäre
                // gerade nichts passiert.
                <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
                  Von dieser Seite kommen noch keine Lebenszeichen — sie lädt vermutlich noch die vorige Fassung des
                  Trackers. Spätestens nach 24 Stunden ist das von allein vorbei.
                </p>
              )}
              {data.online && data.jetzt && (
                <p className="mt-3 text-xs text-gray-500">
                  Gezählt bis zum letzten Lebenszeichen um {uhrzeit(data.zuletzt)} Uhr — die Uhr läuft nicht weiter,
                  wenn der Tab zu ist. Die gemessene Lesezeit folgt, sobald er die Seite verlässt; sie zählt nur die
                  Zeit, in der die Seite wirklich sichtbar war.
                </p>
              )}
            </div>

            <div className="border-t border-gray-100 bg-gray-50 px-5 py-3.5">
              <div className="flex gap-2.5">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <p className="text-xs leading-relaxed text-gray-600">
                  Mehr gibt es nicht: kein Name, keine Firma, keine IP, keine Stadt. Der Schlüssel dieser Sitzung
                  wechselt um Mitternacht — kommt dieselbe Person morgen wieder, ist sie ein neuer Eintrag.
                </p>
              </div>
            </div>
          </>
          );
        })()}
      </aside>
    </div>
  );
}
